import type { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import type { BatchCreateContactMetierDto, ContactMetier } from "models";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { grey } from "@mui/material/colors";
import { MetierBox, PerimetreBox, PosteBox, TerritoireBox } from "components/Business";
import { contactsMetiersUtil, getPlural } from "utils";
import { ContactMetierResume } from "components/ContactMetierResume";

interface CreationEnMasseConfirmationDialogProps {
  contactsMetiersToCreate?: BatchCreateContactMetierDto;
  similarContactsMetiers?: Array<ContactMetier>;
  onConfirm?: () => void;
  onClose: () => void;
  isOpen: boolean;
}

export function CreationEnMasseConfirmationDialog({
  contactsMetiersToCreate,
  similarContactsMetiers,
  onConfirm,
  onClose,
  isOpen,
}: Readonly<CreationEnMasseConfirmationDialogProps>): ReactNode {
  const similarCmsNumber = similarContactsMetiers?.length ?? 0;

  if (contactsMetiersToCreate == null || contactsMetiersToCreate.contacts.length === 0) {
    return;
  }

  const referenceContactMetier = contactsMetiersToCreate.contacts[0] as ContactMetier;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>
        Êtes-vous sûr(e) de vouloir créer ces{" "}
        <Typography variant="inherit" component="span" color="primary">
          {contactsMetiersToCreate.contacts.length}
        </Typography>{" "}
        Contacts Métiers ?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Grid container spacing={2} size={12}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Stack direction="row" spacing={1}>
                <Typography sx={{ color: grey[500] }}>Métier :</Typography>
                <MetierBox metier={referenceContactMetier.metier} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography sx={{ color: grey[500] }}>Poste :</Typography>
                <PosteBox poste={referenceContactMetier.poste} />
              </Stack>

              {contactsMetiersUtil.isCommune(referenceContactMetier) && (
                <Stack spacing={1}>
                  <Typography sx={{ color: grey[500] }}>Communes :</Typography>
                  <Grid container>
                    {contactsMetiersToCreate.contacts.map(({ commune }) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={commune?.id}>
                        <Typography>
                          {commune?.libelle}{" "}
                          <span style={{ color: grey[500] }}>({commune?.id.substring(0, 2)})</span>
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              )}
              {contactsMetiersUtil.isCommuneAssocieeDeleguee(referenceContactMetier) && (
                <Stack spacing={1}>
                  <Typography sx={{ color: grey[500] }}>Commune associée/déléguées :</Typography>
                  <Grid container>
                    {contactsMetiersToCreate.contacts.map(({ cad }) => (
                      <Grid size={6} key={cad?.id}>
                        <Typography>
                          {cad?.libelle}{" "}
                          <span style={{ color: grey[500] }}>({cad?.id.substring(0, 2)})</span>
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              )}
              {contactsMetiersUtil.isPerimetre(referenceContactMetier) && (
                <Stack spacing={1}>
                  <Typography sx={{ color: grey[500] }}>Périmètres :</Typography>
                  {contactsMetiersToCreate.contacts.map(({ perimetre }) => (
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    <PerimetreBox perimetre={perimetre!} key={perimetre?.id} />
                  ))}
                </Stack>
              )}
              {contactsMetiersUtil.isTerritoire(referenceContactMetier) && (
                <Stack spacing={1}>
                  <Typography sx={{ color: grey[500] }}>Territoires :</Typography>
                  <Grid container>
                    {contactsMetiersToCreate.contacts.map(({ territoire }) => (
                      <Grid size={6} key={territoire?.id}>
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <TerritoireBox territoire={territoire!} key={territoire?.id} />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              )}
            </Stack>
          </Grid>
        </DialogContentText>

        {similarCmsNumber > 0 && (
          <DialogContentText sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Typography sx={{ fontWeight: "bold" }}>
                ATTENTION ! {getPlural(similarCmsNumber, "Plusieurs", "Un")} Contact
                {getPlural(similarCmsNumber)} Métier
                {getPlural(similarCmsNumber)} existe{getPlural(similarCmsNumber, "nt")} déjà avec
                des informations similaires :
              </Typography>
              <Grid container spacing={1}>
                {similarContactsMetiers?.map((cm) => (
                  <ContactMetierResume key={cm.id} contactMetier={cm} />
                ))}
              </Grid>
            </Grid>
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Annuler
        </Button>
        <Button
          onClick={() => {
            onConfirm?.();
            onClose();
          }}
          variant="contained"
          autoFocus
          loadingPosition="start"
          startIcon={<GroupAddIcon />}>
          Oui, créer les {contactsMetiersToCreate.contacts.length} Contacts Métiers
        </Button>
      </DialogActions>
    </Dialog>
  );
}
